import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { MagasinControllerApi } from '../../api/services/magasin-controller-api';
import { Article } from '../../api/models/article';
import { Magasin } from '../../api/models/magasin';
import { GetArticlesByMagasin$Params } from '../../api/fn/magasin-controller/get-articles-by-magasin';
import { GetMagasinByTechnicien$Params } from '../../api/fn/magasin-controller/get-magasin-by-technicien';
import { SortirArticle$Params } from '../../api/fn/magasin-controller/sortir-article';
import { HttpContext } from '@angular/common/http';

/**
 *   Comment l'utiliser (Activer / Désactiver à la volée)
 *
 *   Pour passer en mode simulé ou repasser sur l'API réelle, il vous suffit de définir une clé dans le localStorage de votre navigateur (via la console Web F12 ou vos outils de stockage) :
 *
 *   Pour ACTIVER le Mock :
 *   Ouvrez la console de votre navigateur (F12) et tapez :
 *    1 localStorage.setItem('use_mock_magasin', 'true');
 *   Puis actualisez la page (F5). Vos magasins et vos listes d'articles utiliseront désormais des données de démonstration simulées localement !
 *
 *   Pour DÉSACTIVER le Mock (revenir aux vrais appels API) :
 *   Ouvrez la console et tapez :
 *    1 localStorage.removeItem('use_mock_magasin');
 *   Puis actualisez la page (F5). L'application interrogera à nouveau le vrai backend de manière habituelle !
 */
@Injectable({ providedIn: 'root' })
export class MockMagasinControllerApi extends MagasinControllerApi {
  override getMagasinByTechnicien(
    params: GetMagasinByTechnicien$Params,
    context?: HttpContext
  ): Observable<Array<Magasin>> {
    if (localStorage.getItem('use_mock_magasin') === 'true') {
      console.warn('[MockMagasinControllerApi] API simulée active pour getMagasinByTechnicien !');
      const mockMagasins: Array<Magasin> = [
        {
          id: 'MAG-PRINCIPAL',
          label: 'Magasin Central - Paris 15 (Simulé)',
          quantityStock: 1420,
        },
        {
          id: 'MAG-SECONDAIRE',
          label: 'Dépôt Technique - Lyon (Simulé)',
          quantityStock: 450,
        },
        {
          id: 'MAG-URGENCE',
          label: "Coffre Véhicule d'Intervention (Simulé)",
          quantityStock: 75,
        },
      ];
      return of(mockMagasins).pipe(delay(600));
    }
    return super.getMagasinByTechnicien(params, context);
  }

  override getArticlesByMagasin(
    params: GetArticlesByMagasin$Params,
    context?: HttpContext
  ): Observable<Array<Article>> {
    if (localStorage.getItem('use_mock_magasin') === 'true') {
      const magasinId = params['id-magasin'];
      console.warn(`[MockMagasinControllerApi] API simulée active pour les articles du magasin : ${magasinId}`);

      const mockArticles: Array<Article> = [
        {
          codeArticle: 'ART-FILT-01',
          libelleArticle: 'Filtre à air G4 plissé 400x500x48',
          quantityStockArticle: 12,
          fabricantArticle: 'Camfil',
          refFabricantArticle: 'CF-G4-400500',
          familleArticle: 'Filtration',
          rangementArticle: 'Allée A - Étagère 3',
        },
        {
          codeArticle: 'ART-FILT-02',
          libelleArticle: 'Filtre à poches F7 592x592x600',
          quantityStockArticle: 8,
          fabricantArticle: 'Viledon',
          refFabricantArticle: 'VL-F7-592592',
          familleArticle: 'Filtration',
          rangementArticle: 'Allée A - Étagère 4',
        },
        {
          codeArticle: 'ART-VANN-10',
          libelleArticle: 'Vanne papillon DN80 PN16 fonte',
          quantityStockArticle: 3,
          fabricantArticle: 'KSB',
          refFabricantArticle: 'KSB-BOAX-80',
          familleArticle: 'Robinetterie',
          rangementArticle: 'Allée B - Étagère 1',
        },
        {
          codeArticle: 'ART-POMP-05',
          libelleArticle: 'Circulateur simple à rotor noyé Stratos MAXO 30',
          quantityStockArticle: 2,
          fabricantArticle: 'Wilo',
          refFabricantArticle: 'WL-MAXO-30',
          familleArticle: 'Pompes & Circulateurs',
          rangementArticle: 'Zone Gros Matériel',
        },
        {
          codeArticle: 'ART-SOND-21',
          libelleArticle: 'Sonde de température à câble PT100 IP65',
          quantityStockArticle: 15,
          fabricantArticle: 'S+S Regeltechnik',
          refFabricantArticle: 'SS-PT100-2M',
          familleArticle: 'Régulation',
          rangementArticle: 'Tiroir Électronique C-2',
        },
        {
          codeArticle: 'ART-BOUT-99',
          libelleArticle: 'Contacteur auxiliaire 4 NO 24VAC',
          quantityStockArticle: 0,
          fabricantArticle: 'Schneider Electric',
          refFabricantArticle: 'LC1D09B7',
          familleArticle: 'Électricité',
          rangementArticle: 'Tiroir Électricité A-5',
        },
      ];

      if (magasinId === 'MAG-URGENCE') {
        return of(mockArticles.slice(4)).pipe(delay(500));
      }

      return of(mockArticles).pipe(delay(800));
    }
    return super.getArticlesByMagasin(params, context);
  }

  override sortirArticle(params: SortirArticle$Params, context?: HttpContext): Observable<{}> {
    if (localStorage.getItem('use_mock_magasin') === 'true') {
      console.warn(
        `[MockMagasinControllerApi] API simulée active pour la sortie d'un article. Magasin: ${params['id-magasin']}, Article: ${params['code-article']}, Quantité: ${params.quantity}, Technicien: ${params['dk-code-ressource']}`
      );
      return of({}).pipe(delay(500));
    }
    return super.sortirArticle(params, context);
  }
}
